import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import * as uiActions from '~actions/ui-actions';

import * as identityProviderAPI from '~api/identity-providers';

import * as dialogsTypes from '~components/dialogs/dialog-types';
import ActionButton from '~components/general/action-button';
import GetMore from '~components/general/get-more';
import GettingMore from '~components/general/getting-more';
import IString from '~components/general/i-string';
import Search from '~components/general/search';
import Time from '~components/general/time';
import IdentityProviderLink from '~components/identity-providers/identity-provider-link';
import { IdentityProvider } from '~components/identity-providers/identity-provider-prop-types';

import { translate } from '~i18n/localize';

const IdentityProviderSearchResult = ({ identityProvider }) =>
  <tr>
    <td>{identityProvider.id}</td>
    <td><IdentityProviderLink identityProvider={identityProvider} /></td>
    <td><Time value={identityProvider.created_time} /></td>
    <td><Time value={identityProvider.updated_time} /></td>
  </tr>;

IdentityProviderSearchResult.propTypes = {
  identityProvider: IdentityProvider.isRequired
};

export default function IdentityProviderSearch() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const query = params.get('query');

  const [ loading, setLoading ] = useState(true);
  const [ results, setResults ] = useState(null);

  const identityProviders = results?.identity_providers;
  const total = results?.total || 0;
  const offset = identityProviders?.length || 0;
  const hasMore = offset < total;

  const empty = !loading && !!results && results.length === 0;
  let listStatus = null;

  const fetchMoreIdentityProviders = useCallback(async (query, offset) => {
    try {
      setLoading(true);
      const { data } = await identityProviderAPI.listIdentityProviders(query, offset);
      if (offset > 0) {
        setResults(prev => ({ identity_providers: [ ...prev.identity_providers, ...data.identity_providers ], total: data.total }));
      } else {
        setResults(data);
      }
    } catch (error) {
      dispatch(uiActions.setErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [ dispatch ]);

  if (loading) {
    // getting more spinner
    listStatus = <GettingMore label={translate('general.loading')} />;
  } else if (hasMore) {
    // get more "button"
    listStatus = (
      <GetMore
        autoFire
        label={translate('identityProvider.search.getMore')}
        onClick={() => fetchMoreIdentityProviders(query, offset)}
      />
    );
  }

  // Anytime the query changes, we need to do a new search
  useEffect(() => {
    (async () => { await fetchMoreIdentityProviders(query, 0); })();
  }, [ fetchMoreIdentityProviders, query ]);

  const handleSearch = (newSearch) => {
    history.replace({ search: newSearch ? `?query=${encodeURIComponent(newSearch)}` : '' });
  };

  const handleAdd = () => {
    dispatch(uiActions.openDialog({
      type: dialogsTypes.IDENTITY_PROVIDER_DIALOG,
      onSubmit: async (formData) => {
        if (formData) {
          try {
            const { data } = await identityProviderAPI.createIdentityProvider(formData);
            history.push(`/identity-providers/${data.id}`, { identityProvider: data });
            dispatch(uiActions.closeDialog());
          } catch (error) {
            dispatch(uiActions.setErrorMessage(error));
          }
        }
      }
    }));
  };

  return (
    <>
      <div className="sectionTitle flex">
        <IString stringKey={`identityProvider.search.title.${query ? 'results' : 'prompt' }`} />
        <div className="actionBar inline">
          <ActionButton className="new noTitle" title={translate('identityProvider.search.addIdentityProvider')} onClick={handleAdd} />
        </div>
      </div>
      <div className="section">
        <Search value={query} submit={handleSearch} />
      </div>
      <div className="smallMarginBelow">
        <div className="right">
          { total !== undefined && <IString stringKey="general.total" placeholders={{ total }} /> }
        </div>
      </div>
      <div className="tableContainer">
        {empty
          ? <div className="emptyListMessage"><IString stringKey="general.empty" /></div>
          : <table className="contentListing">
            <thead>
              <tr>
                <th><IString stringKey="identityProvider.search.id" /></th>
                <th><IString stringKey="general.name" /></th>
                <th><IString stringKey="general.created" /></th>
                <th><IString stringKey="general.updated" /></th>
              </tr>
            </thead>
            <tbody>
              {identityProviders?.map(idP => <IdentityProviderSearchResult identityProvider={idP} key={idP.id} />)}
            </tbody>
          </table>}
      </div>
      {listStatus}
    </>
  );
}
